import React, { useState, useEffect, useRef } from 'react';
import { getDashboardStatistics } from '../../utils/apiHelper';
import { formatPrice } from '../../utils/formatHelper';
import InfoTooltip from './InfoTooltip';
import { useDashboardData } from './Dashboard';
import { Chart, registerables } from 'chart.js';
Chart.register(...registerables);

const SPARKLINE_OPTIONS = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: { legend: { display: false }, tooltip: { enabled: false } },
  scales: { x: { display: false }, y: { display: false } },
  elements: { point: { radius: 0 }, line: { tension: 0, borderWidth: 2 } }
};

const Sparkline = ({ data = [], color = '#10b981' }) => {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);

  useEffect(() => {
    if (!canvasRef.current || data.length === 0) return;
    if (chartRef.current) chartRef.current.destroy();

    chartRef.current = new Chart(canvasRef.current, {
      type: 'line',
      data: {
        labels: data.map((_, i) => i),
        datasets: [{
          data,
          borderColor: color,
          backgroundColor: color + '20',
          fill: true
        }]
      },
      options: SPARKLINE_OPTIONS
    });

    return () => { chartRef.current?.destroy(); };
  }, [data, color]);

  return <canvas ref={canvasRef} style={{ height: '32px', width: '100%' }} />;
};

const KpiCards = () => {
  const [subStats, setSubStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const { charts: chartStats, customers: custStats, deliveries: deliveryStats } = useDashboardData();

  useEffect(() => {
    const load = async () => {
      try {
        const subRes = await getDashboardStatistics.subscriptions();
        if (subRes?.success) setSubStats(subRes.statistics);
      } catch (err) {
        console.error('KPI load error:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) {
    return (
      <div className="dashboard-section">
        <div className="dashboard-section-title">
          <i className="fas fa-chart-line"></i> Performans
        </div>
        <div className="section-loading">
          <div className="spinner-border spinner-border-sm me-2" role="status"></div>
          Yükleniyor...
        </div>
      </div>
    );
  }

  if (!subStats) return null;

  const renderTrend = (value, suffix = '') => {
    if (value === 0 || value === null || value === undefined) {
      return <span className="kpi-trend-neutral">— Değişim yok</span>;
    }
    const isUp = value > 0;
    return (
      <span className={isUp ? 'kpi-trend-up' : 'kpi-trend-down'}>
        <i className={`fas fa-arrow-${isUp ? 'up' : 'down'} me-1`}></i>
        {isUp ? '+' : ''}{value}{suffix} vs geçen ay
      </span>
    );
  };

  const tips = subStats.tooltips || {};

  const cards = [
    {
      title: 'Aylık Gelir (MRR)',
      value: subStats.revenueStats?.mrr !== undefined ? formatPrice(subStats.revenueStats.mrr) : null,
      trend: subStats.revenueStats?.mrrGrowth,
      trendSuffix: '%',
      extra: subStats.revenueStats?.monthlyRevenue > 0
        ? `Bu ay gerçekleşen: ${formatPrice(subStats.revenueStats.monthlyRevenue)}`
        : null,
      sparkData: chartStats?.revenueGrowth?.data || [],
      sparkColor: '#10b981',
      tooltip: tips.mrr
    },
    {
      title: 'Abonelikler',
      value: subStats.active,
      trend: subStats.monthlyGrowth,
      trendSuffix: '%',
      extra: (() => {
        const parts = [];
        // Paid vs free breakdown
        const free = subStats.freeSubscriptions;
        if (free > 0) parts.push(`${subStats.paidSubscriptions} ücretli, ${free} ücretsiz`);
        // New/cancelled this month
        const newSubs = subStats.newSubscriptionsThisMonth;
        const cancelled = subStats.cancelledThisMonth;
        const monthParts = [];
        if (newSubs > 0) monthParts.push(`+${newSubs} yeni`);
        if (cancelled > 0) monthParts.push(`-${cancelled} iptal`);
        if (monthParts.length > 0) parts.push(monthParts.join(', ') + ' bu ay');
        return parts.length > 0 ? parts.join(' · ') : null;
      })(),
      sparkData: chartStats?.subscriptionGrowth?.datasets?.active || [],
      sparkColor: '#3b82f6',
      tooltip: tips.active
    },
    {
      title: 'Kayıp Oranı',
      value: subStats.churnRate !== undefined ? `%${subStats.churnRate}` : null,
      trend: subStats.previousChurnRate !== undefined
        ? -(subStats.churnRate - subStats.previousChurnRate)
        : null,
      trendSuffix: ' puan',
      extra: subStats.revenueStats?.churnedMrr > 0
        ? `Kaybedilen: ${formatPrice(subStats.revenueStats.churnedMrr)}/ay`
        : null,
      sparkData: [],
      sparkColor: '#ef4444',
      tooltip: tips.churnRate,
      invertTrend: true
    },
    {
      title: 'Kargolanmayı Bekleyen Tutar',
      value: deliveryStats?.totalPendingValue !== undefined ? formatPrice(deliveryStats.totalPendingValue) : '₺0,00',
      trend: null, // Since we don't have historical pending value, we skip trend
      trendSuffix: '',
      extra: deliveryStats?.pending > 0 ? `${deliveryStats.pending} sipariş işlenmeyi bekliyor` : 'Tüm siparişler gönderildi',
      sparkData: [],
      sparkColor: '#f59e0b',
      tooltip: 'Şu an depoda hazırlanmayı bekleyen tüm siparişlerin toplam finansal değeridir.',
      invertTrend: false
    }
  ];

  return (
    <div className="dashboard-section">
      <div className="dashboard-section-title">
        <i className="fas fa-chart-line"></i> Bu Ayın Performansı
      </div>
      <div className="kpi-cards-grid">
        {cards.map((card, i) => (
          <div key={i} className="kpi-card">
            <div className="kpi-card-header">
              <div className="kpi-card-title">
                {card.title}
                {card.tooltip && <InfoTooltip text={card.tooltip} />}
              </div>
            </div>
            <div className="kpi-card-value">{card.value}</div>
            <div className="kpi-card-comparison">
              {card.trend !== null && card.trend !== undefined
                ? renderTrend(card.invertTrend ? card.trend : card.trend, card.trendSuffix)
                : <span className="kpi-trend-neutral">— Karşılaştırma verisi yok</span>
              }
            </div>
            {card.extra && (
              <div style={{ fontSize: '0.75rem', color: 'var(--admin-text-secondary)', marginTop: '0.25rem' }}>
                {card.extra}
              </div>
            )}
            {card.sparkData.length > 0 && (
              <div className="kpi-sparkline">
                <Sparkline data={card.sparkData} color={card.sparkColor} />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default KpiCards;

